import type { RechargeDetailDto, RechargeOrderDetailVo } from '#services/api';
import { FIELD_KEYS } from '../constants';
import type { TextAreaWithUploadValue } from '../types';

/** 把 attachment_ids 回填为 TextAreaWithUpload files 结构（仅 id，附件详情需 detail 接口） */
function toReasonValue(
  text?: string,
  ids?: number[],
  itemKey: string = ''
): TextAreaWithUploadValue {
  return {
    text: text || '',
    files: (ids || []).map((id) => ({ id })),
    itemKey,
  };
}

/**
 * 把 draft 返回的 RechargeDetailDto 映射为表单初始值。
 * 金额类字段（order_amount/budget_amount/quote_price）配合 currencyType 组件，
 * 需在 form 中同时预填 currency（币种只读，selectDisabled），组件内做千分位展示。
 * execute_date_start/execute_date_end 保持字符串（执行日期区间以 input + showComponent 拼接展示，不做 dayjs 转换）；
 * 超充/提前充值原因组装为 TextAreaWithUpload 值。
 */
export function buildRechargeInitialValues(
  detail: RechargeDetailDto
): Record<string, unknown> {
  return {
    // 只读单据上下文字段（FormModel descriptions 模式渲染：二级单号/三级单号/供应商名称/充值单号）
    l2_demand_id: detail.l2_demand_id,
    l3_demand_id: detail.l3_demand_id,
    supplier_name: detail.supplier_name,
    order_no: detail.order_no,
    order_amount: detail.order_amount,
    account_id_name: detail.account_id_name,
    channel: detail.channel,
    platform: detail.platform,
    budget_amount: detail.budget_amount,
    budget_currency: detail.budget_currency,
    quote_price: detail.quote_price,
    quote_currency: detail.quote_currency,
    currency: detail.currency,
    execute_date_start: detail.execute_date_start,
    execute_date_end: detail.execute_date_end,
    is_over_charge: detail.is_over_charge,
    is_early_recharge: detail.is_early_recharge,
    optimizer_ids: detail.optimizer_ids,
    over_charge_reason: toReasonValue(
      detail.over_charge_reason,
      detail.over_charge_attachment_ids,
      FIELD_KEYS.OVER_CHARGE_REASON
    ),
    early_recharge_reason: toReasonValue(
      detail.early_recharge_reason,
      detail.early_recharge_attachment_ids,
      FIELD_KEYS.EARLY_RECHARGE_REASON
    ),
  };
}

/**
 * 审批详情（RechargeOrderDetailVo）→ 表单值映射（RechargeFormModel view 模式用）。
 *
 * 与 {@link buildRechargeInitialValues}（发起态 draft DTO）不同：审批详情 VO 的金额字段名是
 * `amount`（而非 draft 的 `order_amount`），且只读字段（单号/二级/三级/供应商/渠道/平台）
 * 由 RechargeFormModel 的 showComponent 直接读 renderData.detailData 展示，不落 form 值。
 * 这里仅映射「依赖 form 值」的字段：
 * - order_amount + currency：CurrencyType（VIRTUAL_KEY_ORDER_AMOUNT formItemKeyMap）view 模式读 form 值
 * - account_id_name：input view 模式读 form 值
 * - optimizer_ids：优化师映射为对象数组（{ user_id, name }），对齐退款 optimizers 字段的 value 结构，
 *   供 view 模式 showComponent 读 value 显示名单（不依赖 renderData 闭包，规避 renderItemCols useMemo
 *   未把 renderData 纳入依赖导致的闭包过期问题）
 */
export function buildRechargeConfirmValues(
  detail: Pick<
    RechargeOrderDetailVo,
    | 'amount'
    | 'currency'
    | 'account_id_name'
    | 'optimizers'
    | 'budget_currency'
    | 'quote_currency'
  >
): Record<string, unknown> {
  return {
    order_amount: detail.amount,
    currency: detail.currency,
    account_id_name: detail.account_id_name,
    budget_currency: detail.budget_currency,
    quote_currency: detail.quote_currency,
    optimizer_ids: (detail.optimizers || []).map((o) => ({
      user_id: o?.id,
      name: o?.name ?? '',
      user_name: o?.name ?? '',
    })),
  };
}

/**
 * 退回后重新发起（待发起态）→ 详情 VO 映射为可编辑表单值（RechargeFormModel edit 模式用）。
 *
 * 与 {@link buildRechargeInitialValues}（发起态 draft DTO）同构，但来源是 detail 接口的
 * {@link RechargeOrderDetailVo}（审批详情语义）：金额字段名 `amount`（非 `order_amount`）、
 * 优化师为对象数组 `optimizers`（非 id 数组 `optimizer_ids`）、附件为 {@code AttachmentVO[]}
 * （含 blob，非 attachment_ids）。附件 VO 结构 {id, blob} 与 TextAreaWithUploadValue.files 兼容，直接透传。
 * 待发起态重新发起走 confirm 接口提交（可编辑字段 + 重新核算）。
 */
export function buildRechargeEditValues(
  detail: RechargeOrderDetailVo
): Record<string, unknown> {
  return {
    l2_demand_id: detail.l2_demand_id,
    l3_demand_id: detail.l3_demand_id,
    supplier_name: detail.supplier_name,
    order_no: detail.order_no,
    order_amount: detail.amount,
    currency: detail.currency,
    account_id_name: detail.account_id_name,
    channel: detail.channel,
    platform: detail.platform,
    budget_amount: detail.budget_amount,
    budget_currency: detail.budget_currency,
    quote_price: detail.quote_price,
    quote_currency: detail.quote_currency,
    execute_date_start: detail.execute_date_start,
    execute_date_end: detail.execute_date_end,
    is_over_charge: detail.is_over_charge,
    is_early_recharge: detail.is_early_recharge,
    optimizer_ids: (detail.optimizers || []).map((o) => o?.id),
    over_charge_reason: {
      text: detail.over_charge_reason || '',
      files: detail.over_charge_attachments || [],
      itemKey: FIELD_KEYS.OVER_CHARGE_REASON,
    },
    early_recharge_reason: {
      text: detail.early_recharge_reason || '',
      files: detail.early_recharge_attachments || [],
      itemKey: FIELD_KEYS.EARLY_RECHARGE_REASON,
    },
  };
}
